import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Card } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { MapPin, Plus, Edit, Trash2, Home, Briefcase } from 'lucide-react';
import { toast } from '../../hooks/use-toast';

const Addresses = () => {
  const { user } = useAuth();
  const [addresses, setAddresses] = useState([
    {
      id: 1,
      type: 'home',
      label: 'Casa',
      recipient: user?.name,
      street: 'Av. Corrientes 1234',
      city: 'Buenos Aires',
      state: 'CABA',
      zipCode: '1043',
      phone: '+54 9 11 1234-5678',
      isDefault: true
    }
  ]);
  const [showDialog, setShowDialog] = useState(false);
  const [editingAddress, setEditingAddress] = useState(null);
  const [formData, setFormData] = useState({
    type: 'home',
    label: '',
    recipient: '',
    street: '',
    city: '',
    state: '',
    zipCode: '',
    phone: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (editingAddress) {
      setAddresses(addresses.map(addr => 
        addr.id === editingAddress.id ? { ...formData, id: addr.id, isDefault: addr.isDefault } : addr
      ));
      toast({
        title: '¡Dirección actualizada!',
        description: 'La dirección ha sido modificada exitosamente',
      });
    } else {
      setAddresses([...addresses, { ...formData, id: Date.now(), isDefault: false }]);
      toast({
        title: '¡Dirección agregada!',
        description: 'La nueva dirección ha sido guardada',
      });
    }
    
    resetForm();
  };

  const resetForm = () => {
    setFormData({
      type: 'home',
      label: '',
      recipient: '',
      street: '',
      city: '',
      state: '',
      zipCode: '',
      phone: ''
    });
    setEditingAddress(null);
    setShowDialog(false);
  };

  const handleEdit = (address) => {
    setEditingAddress(address);
    setFormData(address);
    setShowDialog(true);
  };

  const handleDelete = (id) => {
    setAddresses(addresses.filter(addr => addr.id !== id));
    toast({
      title: 'Dirección eliminada',
      description: 'La dirección ha sido removida',
    });
  };

  const setAsDefault = (id) => {
    setAddresses(addresses.map(addr => ({
      ...addr,
      isDefault: addr.id === id
    })));
    toast({
      title: 'Dirección predeterminada actualizada',
    });
  };

  return (
    <div className="space-y-6">
            <Card className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h1 className="text-2xl font-bold">Mis Direcciones</h1>
                <Button
                  onClick={() => setShowDialog(true)}
                  className="bg-zl-orange hover:bg-zl-orange-600"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Agregar dirección
                </Button>
              </div>
              
              <div className="space-y-4">
                {addresses.map(address => (
                  <Card key={address.id} className={`p-4 ${
                    address.isDefault ? 'border-2 border-zl-orange' : ''
                  }`}>
                    <div className="flex items-start justify-between">
                      <div className="flex items-start space-x-3">
                        <div className="w-10 h-10 bg-zl-orange-100 rounded-full flex items-center justify-center">
                          {address.type === 'home' ? (
                            <Home className="w-5 h-5 text-zl-orange" />
                          ) : (
                            <Briefcase className="w-5 h-5 text-zl-orange" />
                          )}
                        </div>
                        <div>
                          <div className="flex items-center space-x-2 mb-1">
                            <h3 className="font-semibold">{address.label || (address.type === 'home' ? 'Casa' : 'Trabajo')}</h3>
                            {address.isDefault && (
                              <span className="px-2 py-0.5 bg-zl-orange text-white text-xs rounded">
                                Predeterminada
                              </span>
                            )}
                          </div>
                          <p className="text-gray-700">{address.recipient}</p>
                          <p className="text-gray-600 text-sm">
                            {address.street}<br />
                            {address.city}, {address.state} - {address.zipCode}<br />
                            Tel: {address.phone}
                          </p>
                        </div>
                      </div>
                      <div className="flex space-x-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEdit(address)}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDelete(address.id)}
                          disabled={address.isDefault}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    {!address.isDefault && (
                      <div className="mt-3 pt-3 border-t">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setAsDefault(address.id)}
                        >
                          Establecer como predeterminada
                        </Button>
                      </div>
                    )}
                  </Card>
                ))}
                
                {addresses.length === 0 && (
                  <div className="text-center py-12">
                    <MapPin className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                    <p className="text-gray-500 mb-4">No tienes direcciones guardadas</p>
                    <Button onClick={() => setShowDialog(true)} className="bg-zl-orange hover:bg-zl-orange-600">
                      <Plus className="w-4 h-4 mr-2" />
                      Agregar tu primera dirección
                    </Button>
                  </div>
                )}
              </div>
            </Card>

      {/* Add/Edit Address Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {editingAddress ? 'Editar dirección' : 'Nueva dirección'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="recipient">Destinatario</Label>
                <Input
                  id="recipient"
                  value={formData.recipient}
                  onChange={(e) => setFormData({ ...formData, recipient: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="phone">Teléfono</Label>
                <Input
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  required
                />
              </div>
            </div>

            <div>
              <Label htmlFor="street">Calle y número</Label>
              <Input
                id="street"
                value={formData.street}
                onChange={(e) => setFormData({ ...formData, street: e.target.value })}
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="city">Ciudad</Label>
                <Input
                  id="city"
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="state">Provincia/Estado</Label>
                <Input
                  id="state"
                  value={formData.state}
                  onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="zipCode">Código postal</Label>
                <Input
                  id="zipCode"
                  value={formData.zipCode}
                  onChange={(e) => setFormData({ ...formData, zipCode: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="label">Etiqueta (opcional)</Label>
                <Input
                  id="label"
                  value={formData.label}
                  onChange={(e) => setFormData({ ...formData, label: e.target.value })}
                  placeholder="Ej: Casa, Oficina"
                />
              </div>
            </div>

            <div className="flex space-x-3 pt-4">
              <Button type="submit" className="bg-zl-orange hover:bg-zl-orange-600">
                {editingAddress ? 'Guardar cambios' : 'Agregar dirección'}
              </Button>
              <Button type="button" variant="outline" onClick={resetForm}>
                Cancelar
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Addresses;
